import { Checkbox, Combobox, Toggles } from '@/components'
import { DropdownNames } from '@/components_deprecated/Select/types'
import { UserOrderBy } from '@/modules/activities'
import { useMassActivityExportForm } from '@/modules/activities/hooks'
import { Module, ModuleOptionsValue } from '@/modules/companies'
import { ExportFormat, ExportOption, ExportType } from '@/modules/exports'
import {
  ExportCard,
  ExportField,
  ExportFormatField,
  ExportMethodField,
  ExportPeriodField,
  ExportUsersField,
} from '@/modules/exports/components/export-form'
import { useModuleConfig } from '@/utils/hooks'
import { useTranslate } from '@ur-solutions/react-hooks'
import { CalendarDays, CalendarRange, TriangleAlert } from 'lucide-react'
import React, { useEffect, useMemo } from 'react'

const SALARY_FORMATS = [
  ExportFormat.POWEROFFICE,
  ExportFormat.VISMA,
  ExportFormat.TWENTYFOUR_SEVEN_OFFICE,
  ExportFormat.TRIPLETEX,
]

const DOWNLOAD_EXCLUDED_FORMATS = [
  ExportFormat.EXCEL,
  ExportFormat.EXCEL_FLAT,
  ExportFormat.PDF_SALARY,
  ...SALARY_FORMATS,
]

const HOURS_EXPORT_TYPES = [
  ExportType.MASS_ACTIVITIES,
  ExportType.MASS_ACTIVITIES_WORK_DAY,
  ExportType.MASS_SUMMARY,
  ExportType.MASS_ACTIVITIES_OVERTIME,
  ExportType.WORK_DAY_REPORT,
  ExportType.OVERTIME_AND_NIGHT_WORK,
]

export const ExportDriverActivitiesCard: React.FC = () => {
  const translations = useTranslate({
    exportWeekly: 'activities.export-monthly-weekly',
    exportAll: 'settings.export-all-driver-activities',
    exportType: 'common.type',
    mergeWithManualTimeEntriesActive:
      'activities.merge-with-manual-time-entries-active',

    orderBy: 'common.order-by',
    includeAbsences: 'settings.include-absences',

    downloadOnlySupportsPdf: 'settings.download-only-supports-pdf',

    month: 'common.month',
    week: 'common.week',

    lastName: 'common.lastName',
    userType: 'common.user-type',
    externalId: 'common.external-id',
  })

  const {
    loading,
    form,
    errors,
    submit,
    updateForm,
    handleExport,
    handler,
    formValid,
    exportTypes,
  } = useMassActivityExportForm()
  const { getModuleOptionStatus: getModuleOptionStatusTimesheets } =
    useModuleConfig(Module.TIMESHEETS)
  const showManualHoursNotice =
    getModuleOptionStatusTimesheets(
      ModuleOptionsValue.MERGE_TIMESHEETS_WITH_ACTIVITIES
    ) && HOURS_EXPORT_TYPES.includes(form.exportType)

  const downloadOnly = form.exportOption === ExportOption.DOWNLOAD

  const orderByOptions = useMemo(
    () => [
      { value: UserOrderBy.FAMILY_NAME, label: translations.lastName },
      { value: UserOrderBy.USER_TYPES, label: translations.userType },
      { value: UserOrderBy.INTERNAL_NUMBER, label: translations.externalId },
    ],
    [translations.externalId, translations.lastName, translations.userType]
  )

  useEffect(() => {
    if (
      form.exportOption === ExportOption.DOWNLOAD &&
      form.exportFormat !== ExportFormat.PDF
    ) {
      updateForm({ exportFormat: ExportFormat.PDF })
    }
  }, [form.exportOption, form.exportFormat, updateForm, form.exportType])

  return (
    <ExportCard
      title={translations.exportAll}
      loading={loading}
      submitDisabled={!formValid}
      onSubmit={submit(handleExport)}
    >
      {showManualHoursNotice && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 md:col-span-2">
          <TriangleAlert className="mt-0.5 size-4 shrink-0 text-amber-600" />
          <span>{translations.mergeWithManualTimeEntriesActive}</span>
        </div>
      )}

      <ExportPeriodField value={form} onChange={value => updateForm(value)} />

      <ExportField label={translations.exportType}>
        <Combobox<ExportType>
          mode="single"
          noSearch
          value={form.exportType}
          items={exportTypes}
          onChange={handler('exportType')}
        />
      </ExportField>

      <ExportMethodField
        value={form.exportOption}
        allowDownload
        onChange={handler('exportOption')}
      />

      <ExportFormatField
        value={form.exportFormat}
        exclude={downloadOnly ? DOWNLOAD_EXCLUDED_FORMATS : SALARY_FORMATS}
        disabled={downloadOnly}
        hint={downloadOnly ? translations.downloadOnlySupportsPdf : null}
        onChange={handler('exportFormat')}
      />

      <ExportUsersField
        value={form.users ?? []}
        error={errors.users}
        dropdownPageName={DropdownNames.ACTIVITIES}
        onChange={handler('users')}
      />

      <ExportField label={translations.exportWeekly}>
        <Toggles
          value={form.weekly ? 'week' : 'month'}
          wide
          options={[
            {
              value: 'month',
              label: (
                <span className="flex items-center gap-1.5">
                  <CalendarDays size={16} />
                  {translations.month}
                </span>
              ),
            },
            {
              value: 'week',
              label: (
                <span className="flex items-center gap-1.5">
                  <CalendarRange size={16} />
                  {translations.week}
                </span>
              ),
            },
          ]}
          onChange={mode => updateForm({ weekly: mode === 'week' })}
        />
      </ExportField>

      <ExportField label={translations.orderBy}>
        <Combobox<UserOrderBy>
          mode="single"
          noSearch
          value={form.orderBy}
          items={orderByOptions}
          onChange={handler('orderBy')}
        />
      </ExportField>

      <label className="flex cursor-pointer items-center gap-2 text-sm text-stone-700 md:col-span-2">
        <Checkbox
          size="md"
          value={form.includeAbsences ?? false}
          onChange={checked =>
            updateForm({ includeAbsences: checked === true })
          }
        />
        {translations.includeAbsences}
      </label>
    </ExportCard>
  )
}
